/* eslint-disable camelcase, @typescript-eslint/naming-convention -- telemetry payload keys mirror wire format */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AIInput } from '../../../../../../ui/organisms/AIInput';
import { useChatWidgetContext } from '../../../../contexts/ChatWidgetContext';
import { ComplianceSection } from '../ComplianceSection';
import { PromptSuggestions } from '../PromptSuggestions';
import { TrainingCoachCard } from '../TrainingCoachCard';
import { requestCoaching } from '../../../../services/coachApi';
import { trackEvent } from '../../../../services/telemetryApi';
import { TELEMETRY_EVENT_COACH_SENT_IMPROVED, TELEMETRY_EVENT_COACH_SENT_ORIGINAL, TELEMETRY_EVENT_COACH_EDITED, TELEMETRY_EVENT_COACH_DISMISSED_WITHOUT_ENGAGEMENT } from '../../../../constants/telemetry';
import './FooterSectionStyles.scss';
const BASE_CLASS = 'ai-chat-footer';
// PROTOTYPE — Training Mode: drafts shorter than this skip coaching entirely
// (a 3-word ask is either fine or too cheap to interrupt over).
const COACH_MIN_DRAFT_LENGTH = 12;
const COACH_GOOD_FLASH_MS = 2500;
export function FooterSection({ onSendMessage, isDisabled = false, isLoading = false, placeholder = 'Ask a question...', onStop, allowAttachments = true, trainingEnabled = false, pageContext, conversationId, persistentSuggestions, persistentSuggestionsLabel, showPersistentSuggestions = false, onChipSelect }) {
    const { retryPrompt, clearRetryPrompt, focusInputSignal, setRetryPrompt } = useChatWidgetContext();
    const [inputKey, setInputKey] = useState(0);
    const retryValueRef = useRef(undefined);
    const inputRef = useRef(null);
    // PROTOTYPE — Training Mode coach state machine
    const [coach, setCoach] = useState(null);
    // Guards against a stale coach response landing after the user moved on
    // (skipped, dismissed, or started another analysis).
    const coachRunIdRef = useRef(0);
    const goodFlashTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    // When a retry prompt arrives, capture the value in a ref (survives React 18 batching),
    // then bump the key to re-mount AIInput with the captured default value.
    useEffect(() => {
        if (retryPrompt) {
            retryValueRef.current = retryPrompt;
            setInputKey((k) => k + 1);
            clearRetryPrompt();
        }
    }, [retryPrompt, clearRetryPrompt]);
    // Focus textarea when the widget opens
    useEffect(() => {
        var _a;
        if (focusInputSignal > 0) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [focusInputSignal]);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (goodFlashTimerRef.current !== null)
                window.clearTimeout(goodFlashTimerRef.current);
        };
    }, []);
    const dismissCoach = useCallback(() => {
        coachRunIdRef.current += 1;
        if (goodFlashTimerRef.current !== null) {
            window.clearTimeout(goodFlashTimerRef.current);
            goodFlashTimerRef.current = null;
        }
        setCoach(null);
    }, []);
    const flashGoodAndSend = useCallback((text) => {
        if (goodFlashTimerRef.current !== null) {
            window.clearTimeout(goodFlashTimerRef.current);
        }
        setCoach({ status: 'sent_good', original: text });
        onSendMessage(text);
        const flashRunId = coachRunIdRef.current;
        goodFlashTimerRef.current = window.setTimeout(() => {
            if (coachRunIdRef.current === flashRunId)
                setCoach(null);
        }, COACH_GOOD_FLASH_MS);
    }, [onSendMessage]);
    const startCoaching = useCallback((text) => {
        coachRunIdRef.current += 1;
        const runId = coachRunIdRef.current;
        if (goodFlashTimerRef.current !== null) {
            window.clearTimeout(goodFlashTimerRef.current);
            goodFlashTimerRef.current = null;
        }
        setCoach({ status: 'analyzing', original: text });
        void requestCoaching(text, pageContext, conversationId).then((result) => {
            if (!isMountedRef.current || coachRunIdRef.current !== runId)
                return; // user skipped/dismissed meanwhile or unmounted
            if (result.verdict === 'improvable' && (result.improved_prompt || result.issues.length > 0)) {
                setCoach({ status: 'review', original: text, result });
            }
            else {
                // "good" verdict (or fail-open) — send immediately with a brief affirmation
                flashGoodAndSend(text);
            }
        });
    }, [pageContext, conversationId, flashGoodAndSend]);
    const handleSubmit = useCallback((payload) => {
        const { text } = payload;
        const files = payload.files.length > 0 ? payload.files : undefined;
        // PROTOTYPE — Training Mode: intercept plain-text drafts for coaching.
        // Attachments, slash commands, and very short drafts bypass the coach.
        const shouldCoach = trainingEnabled && !files && !text.trim().startsWith('/') && text.trim().length >= COACH_MIN_DRAFT_LENGTH;
        if (shouldCoach) {
            startCoaching(text);
            return;
        }
        onSendMessage(text, files);
    }, [onSendMessage, trainingEnabled, startCoaching]);
    const handleSendImproved = useCallback(() => {
        var _a;
        const improved = (_a = coach === null || coach === void 0 ? void 0 : coach.result) === null || _a === void 0 ? void 0 : _a.improved_prompt;
        dismissCoach();
        trackEvent(TELEMETRY_EVENT_COACH_SENT_IMPROVED, { conversation_id: conversationId });
        if (improved)
            onSendMessage(improved);
    }, [coach, dismissCoach, onSendMessage, conversationId]);
    const handleSendOriginal = useCallback(() => {
        const original = coach === null || coach === void 0 ? void 0 : coach.original;
        // "analyzing" → the user skipped via "Send without checking" before ever
        // seeing the review — never engaged with the coach's output. "review" →
        // the user saw the improved version and chose to keep their original
        // anyway — a considered rejection, not a dismissal.
        const event = (coach === null || coach === void 0 ? void 0 : coach.status) === 'analyzing'
            ? TELEMETRY_EVENT_COACH_DISMISSED_WITHOUT_ENGAGEMENT
            : TELEMETRY_EVENT_COACH_SENT_ORIGINAL;
        dismissCoach();
        trackEvent(event, { conversation_id: conversationId });
        if (original)
            onSendMessage(original);
    }, [coach, dismissCoach, onSendMessage, conversationId]);
    const handleEditImproved = useCallback(() => {
        var _a;
        const improved = (_a = coach === null || coach === void 0 ? void 0 : coach.result) === null || _a === void 0 ? void 0 : _a.improved_prompt;
        dismissCoach();
        trackEvent(TELEMETRY_EVENT_COACH_EDITED, { conversation_id: conversationId });
        if (improved)
            setRetryPrompt(improved);
    }, [coach, dismissCoach, setRetryPrompt, conversationId]);
    const handleDismissCoach = useCallback(() => {
        const original = coach === null || coach === void 0 ? void 0 : coach.original;
        dismissCoach();
        trackEvent(TELEMETRY_EVENT_COACH_DISMISSED_WITHOUT_ENGAGEMENT, { conversation_id: conversationId });
        // Put the draft back in the input so dismissing never loses their text
        if (original)
            setRetryPrompt(original);
    }, [coach, dismissCoach, setRetryPrompt, conversationId]);
    const handleSelectPersistentSuggestion = useCallback((prompt) => {
        retryValueRef.current = prompt;
        setInputKey((k) => k + 1);
        window.setTimeout(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
    }, []);
    const showSuggestionsRow = showPersistentSuggestions && !!persistentSuggestions && persistentSuggestions.length > 0 && coach === null;
    return (React.createElement("div", { className: BASE_CLASS },
        showSuggestionsRow && (React.createElement("div", { className: `${BASE_CLASS}__suggestions` },
            React.createElement(PromptSuggestions, { suggestions: persistentSuggestions, groupLabel: persistentSuggestionsLabel, onChipSelect: onChipSelect, onSelectPrompt: handleSelectPersistentSuggestion }))),
        coach !== null && (React.createElement(TrainingCoachCard, { status: coach.status, original: coach.original, result: coach.result, onSendImproved: handleSendImproved, onSendOriginal: handleSendOriginal, onEditImproved: handleEditImproved, onDismiss: handleDismissCoach })),
        React.createElement(AIInput, { key: inputKey, placeholder: placeholder, defaultValue: retryValueRef.current, disabled: isDisabled, isLoading: isLoading, autoFocus: true, textareaRef: inputRef, allowAttachments: allowAttachments, onSubmit: handleSubmit, onStop: onStop }),
        React.createElement(ComplianceSection, null)));
}
